/**
 * Popover Positioner
 *
 * Possible attributes:
 * - data-anchor="<string>": Optional. ID of element to stick to. If not defined, next parent of trigger element.
 * - data-position="left|center|right top|center|bottom": Where to position the popover depending on the anchor.
 * - data-offset="<px>": Pixel of the offset to the anchor and to viewport edges.
 * - data-reposition: Boolean. If reaching the viewport, reconsider positioning.
 *
 * - Automatic discovery: Detects all `[popover]` elements, including ones rendered later by React, via a MutationObserver.
 * - Anchor resolution:
 *    1. Uses data-anchor (ID) if present.
 *    2. Else, falls back to the closest ancestor with position != static.
 *    3. Else, defaults to document.body.
 * - Top-layer aware positioning: Positions the open popover using viewport coordinates with position: fixed, compatible with HTML Popover's top layer.
 * - Single-popover handling: Assumes one popover open at a time; only the currently open one is measured and repositioned.
 * - Position API: Supports data-position="left|center|right top|center|bottom" with the exact growth semantics you defined (left/right grow horizontally from the specified side; top/bottom grow vertically from the specified side; center centers on the axis).
 * - Offset API: Supports data-offset="<px>" (pixels only). The popover never drifts farther from the anchor than this offset—it stays "docked" like a tooltip.
 * - Repositioning logic (optional): When data-reposition is present and the popover would collide with the viewport, it tries the next best positions along the colliding axis (e.g., from right top → right center → right bottom; analogous for horizontal collisions). If nothing fits (popover too large), it keeps the original position.
 * - Scroll + resize responsiveness: Recomputes position on:
 *    - window resize,
 *    - window scroll,
 *    - scroll of nested scroll containers of the anchor (overflow auto|scroll and actually scrollable),
 * - anchor size changes via ResizeObserver.
 * - Open-time measurement: Waits for the popover to be open (listens to toggle) and uses requestAnimationFrame to measure after layout stabilizes.
 * - Collision detection: Checks against the current viewport bounds and triggers the repositioning sequence only when requested (data-reposition).
 * - Event lifecycle & cleanup: Attaches listeners while open and cleans up on close and when nodes are removed from the DOM.
 * - Deliberate exclusions (by design): No handling for transform, overflow clipping, z-index, IntersectionObserver, or non-pixel offsets—matching your constraints.
 */

type HV = 'left' | 'start' | 'center' | 'end' | 'right';
type VV = 'top' | 'start' | 'center' | 'end' | 'bottom';

export type PopoverAlignment = `${HV} ${VV}`;

interface PositionSpec {
  h: HV;
  v: VV;
}

interface ScrollHandler {
  target: HTMLElement | Window;
  handler: EventListener;
}

interface PopoverState {
  onToggle: EventListener;
  scrollHandlers: ScrollHandler[] | null;
  onResize: (() => void) | null;
  ro: ResizeObserver | null;
}

const POPOVER_SELECTOR = '[popover]';

let currentOpenPopover: HTMLElement | null = null;

/** Stores wiring state per popover element to avoid `any` property assignments. */
const popoverStates = new WeakMap<HTMLElement, PopoverState>();

function parsePosition(el: HTMLElement): PositionSpec {
  const raw = (el.getAttribute('data-position') || 'center bottom').trim();
  const [h, v] = raw.split(/\s+/);
  const hv = (h as HV) || 'center';
  const vv = (v as VV) || 'center';
  const validH: HV[] = ['left', 'start', 'center', 'end', 'right'];
  const validV: VV[] = ['top', 'start', 'center', 'end', 'bottom'];
  return {
    h: validH.includes(hv) ? hv : 'center',
    v: validV.includes(vv) ? vv : 'bottom',
  };
}

function parseOffset(el: HTMLElement): number {
  const raw = el.getAttribute('data-offset');
  if (!raw) {
    return 0;
  }
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function hasReposition(el: HTMLElement): boolean {
  return el.hasAttribute('data-reposition');
}

/**
 * Find the "anchor":
 * 1) data-anchor="id" takes precedence
 * 2) Otherwise: next parent with position != static
 * 3) Fallback: body
 */
function resolveAnchor(pop: HTMLElement): HTMLElement {
  const dataAnchor = pop.getAttribute('data-anchor');
  if (dataAnchor) {
    const byId = document.getElementById(dataAnchor);
    if (byId) {
      return byId;
    }
  }
  // Next parent with position != static (DOM chain; also accessible in the top layer)
  let p: HTMLElement | null = pop.parentElement;
  while (p && p !== document.body) {
    const cs = getComputedStyle(p);
    if (cs.position !== 'static') {
      return p;
    }
    p = p.parentElement;
  }
  return document.body;
}

/**
 * Collect all scroll containers (including window) to reposition on scroll.
 */
function getScrollContainers(el: HTMLElement): (HTMLElement | Window)[] {
  const list: (HTMLElement | Window)[] = [window];
  let p: HTMLElement | null = el.parentElement;
  while (p && p !== document.body) {
    const cs = getComputedStyle(p);
    const { overflowY } = cs;
    const { overflowX } = cs;
    const scrollable =
      ((overflowY === 'auto' || overflowY === 'scroll') &&
        p.scrollHeight > p.clientHeight) ||
      ((overflowX === 'auto' || overflowX === 'scroll') &&
        p.scrollWidth > p.clientWidth);
    if (scrollable) {
      list.push(p);
    }
    p = p.parentElement;
  }
  return list;
}

/**
 * Generate candidate order for repositioning according to the specification:
 * - if initially, e.g., "right top", and top collides, then "right center", then "right bottom"
 * - analogous on the horizontal axis
 * Generating axis priorities and calculate their cross product.
 * @internal exported for testing only
 */
export function generatePositionCandidates(
  initial: PositionSpec,
  collideTop: boolean,
  collideBottom: boolean,
  collideLeft: boolean,
  collideRight: boolean
): PositionSpec[] {
  const hBase: HV = initial.h;
  const vBase: VV = initial.v;

  const hOrder: HV[] = [hBase];
  if (collideLeft && hBase === 'left') {
    hOrder.push('center', 'right');
  } else if (collideRight && hBase === 'right') {
    hOrder.push('center', 'left');
  } else if (collideRight && hBase === 'start') {
    hOrder.push('end');
  } else if (collideLeft && hBase === 'end') {
    hOrder.push('start');
  } else if (hBase === 'center' && (collideLeft || collideRight)) {
    hOrder.push(collideLeft ? 'right' : 'left');
  }

  const vOrder: VV[] = [vBase];
  if (collideTop && vBase === 'top') {
    vOrder.push('center', 'bottom');
  } else if (collideBottom && vBase === 'bottom') {
    vOrder.push('center', 'top');
  } else if (collideBottom && vBase === 'start') {
    vOrder.push('end');
  } else if (collideTop && vBase === 'end') {
    vOrder.push('start');
  } else if (vBase === 'center' && (collideTop || collideBottom)) {
    vOrder.push(collideTop ? 'bottom' : 'top');
  }

  // Cross product, but without duplicates
  const out: PositionSpec[] = [];
  hOrder.forEach((h) => {
    vOrder.forEach((v) => {
      if (!out.some((candidate) => candidate.h === h && candidate.v === v)) {
        out.push({ h, v });
      }
    });
  });

  // If no collisions: only initial
  return out.length ? out : [{ h: hBase, v: vBase }];
}

/**
 * Calculate target position (left/top for position: fixed) based on anchor rectangle, popover size, position spec, and offset.
 * "Always stay on element": the distance between the popover and the anchor edge corresponds to the offset.
 * @internal exported for testing only
 */
export function computeFixedCoords(
  anchorRect: DOMRect,
  popSize: { w: number; h: number },
  pos: PositionSpec,
  offset: number
): { left: number; top: number } {
  let left: number;
  let top: number;

  // Horizontal
  // `left` / `right` place the popover entirely outside the anchor.
  // `start` / `end` align an edge of the popover with the matching edge of the
  // anchor (used for the bottom-start / bottom-end menu pattern).
  if (pos.h === 'left') {
    left = anchorRect.left - offset - popSize.w;
  } else if (pos.h === 'start') {
    left = anchorRect.left;
  } else if (pos.h === 'center') {
    left = anchorRect.left + (anchorRect.width - popSize.w) / 2;
  } else if (pos.h === 'end') {
    left = anchorRect.right - popSize.w;
  } else {
    // right
    left = anchorRect.right + offset;
  }

  // Vertical
  if (pos.v === 'top') {
    top = anchorRect.top - offset - popSize.h;
  } else if (pos.v === 'start') {
    top = anchorRect.top;
  } else if (pos.v === 'center') {
    top = anchorRect.top + (anchorRect.height - popSize.h) / 2;
  } else if (pos.v === 'end') {
    top = anchorRect.bottom - popSize.h;
  } else {
    // bottom
    top = anchorRect.bottom + offset;
  }

  return { left: Math.round(left), top: Math.round(top) };
}

function getTranslateXY(el: HTMLElement): { tx: number; ty: number } {
  const t = getComputedStyle(el).transform;
  if (!t || t === 'none') {
    return { tx: 0, ty: 0 };
  }
  try {
    const m = new DOMMatrix(t);
    return { tx: m.m41, ty: m.m42 };
  } catch {
    return { tx: 0, ty: 0 };
  }
}

/**
 * Check viewport collisions for a hypothetical rect.
 * @internal exported for testing only
 */
export function checkCollisions(rect: {
  left: number;
  top: number;
  right: number;
  bottom: number;
}): {
  top: boolean;
  bottom: boolean;
  left: boolean;
  right: boolean;
  any: boolean;
} {
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;
  const left = rect.left < 0;
  const right = rect.right > vw;
  const top = rect.top < 0;
  const bottom = rect.bottom > vh;
  return {
    left,
    right,
    top,
    bottom,
    get any() {
      return this.left || this.right || this.top || this.bottom;
    },
  };
}

/**
 * Set actual styles for the popover (position: fixed in the top layer).
 */
function applyFixed(pop: HTMLElement, coords: { left: number; top: number }) {
  const st = pop.style;
  // For top layer position does not matter, but fixed guarantees viewport coordinates
  st.position = 'fixed';
  st.left = `${coords.left}px`;
  st.top = `${coords.top}px`;
  st.right = 'initial';
  st.bottom = 'initial';
  // Width/Height are NOT forced; growth remains free (important for center)
}

/**
 * Main positioning
 */
function positionPopover(pop: HTMLElement) {
  const anchor = resolveAnchor(pop);
  const pos = parsePosition(pop);
  const offset = parseOffset(pop);
  const wantsReposition = hasReposition(pop);

  const anchorRect = anchor.getBoundingClientRect();

  // Popover must be open for dimensions to be correct
  // (This script is called on 'toggle' after opening.)
  const popRectNow = pop.getBoundingClientRect();
  const popSize = { w: popRectNow.width, h: popRectNow.height };

  // 1) Initial placement
  let coords = computeFixedCoords(anchorRect, popSize, pos, offset);
  const { tx, ty } = getTranslateXY(pop);

  let collide = checkCollisions({
    left: coords.left + tx,
    top: coords.top + ty,
    right: coords.left + tx + popSize.w,
    bottom: coords.top + ty + popSize.h,
  });

  if (wantsReposition && collide.any) {
    // 2) Reposition candidates according to rules
    const candidates = generatePositionCandidates(
      pos,
      collide.top,
      collide.bottom,
      collide.left,
      collide.right
    );

    for (const candidate of candidates) {
      const c = computeFixedCoords(anchorRect, popSize, candidate, offset);
      const col = checkCollisions({
        left: c.left + tx,
        top: c.top + ty,
        right: c.left + tx + popSize.w,
        bottom: c.top + ty + popSize.h,
      });

      if (!col.any) {
        coords = c;
        collide = col;
        break;
      }
    }
  }

  applyFixed(pop, coords);
}

function cleanupPopoverState(pop: HTMLElement) {
  const state = popoverStates.get(pop);
  if (!state) {
    return;
  }
  state.scrollHandlers?.forEach(({ target, handler }) =>
    target.removeEventListener('scroll', handler)
  );
  state.scrollHandlers = null;

  if (state.onResize) {
    window.removeEventListener('resize', state.onResize);
    state.onResize = null;
  }

  state.ro?.disconnect();
  state.ro = null;
}

/** ---- Event-Wiring ---- */

/**
 * For each popover:
 * - listen for "toggle" (opens/closes)
 * - reposition when opened
 * - connect scroll + resize listener (as long as it is open)
 * @internal exported for testing only
 */
export function wirePopover(pop: HTMLElement) {
  if (popoverStates.has(pop)) {
    return;
  }

  const onToggle: EventListener = () => {
    const isOpen = pop.matches(':popover-open');
    if (isOpen) {
      currentOpenPopover = pop;

      // After opening into the next frame, so layout is stable
      requestAnimationFrame(() => {
        positionPopover(pop);
      });

      // Scroll listener for anchor chain + window
      const anchor = resolveAnchor(pop);
      const containers = getScrollContainers(anchor);
      const scrollHandlers: ScrollHandler[] = containers.map(
        (container: HTMLElement | Window) => {
          const handler: EventListener = () => {
            if (currentOpenPopover === pop) {
              positionPopover(pop);
            }
          };
          container.addEventListener('scroll', handler, { passive: true });
          return { target: container, handler };
        }
      );

      // Resize
      const onResize = () => {
        if (currentOpenPopover === pop) {
          positionPopover(pop);
        }
      };
      window.addEventListener('resize', onResize);

      // ResizeObserver for anchor (layout changes)
      const ro = new ResizeObserver(() => {
        if (currentOpenPopover === pop) {
          positionPopover(pop);
        }
      });
      ro.observe(anchor);

      const state = popoverStates.get(pop);
      if (state) {
        state.scrollHandlers = scrollHandlers;
        state.onResize = onResize;
        state.ro = ro;
      }
    } else {
      // Clean up
      if (currentOpenPopover === pop) {
        currentOpenPopover = null;
      }
      cleanupPopoverState(pop);
    }
  };

  popoverStates.set(pop, {
    onToggle,
    scrollHandlers: null,
    onResize: null,
    ro: null,
  });

  pop.addEventListener('toggle', onToggle);
}

/**
 * MutationObserver: detects new/removed popovers (React, dynamic DOM).
 */
const mo = new MutationObserver((mutations) => {
  mutations.forEach((m) => {
    m.addedNodes.forEach((n) => {
      if (n instanceof HTMLElement) {
        if (n.matches?.(POPOVER_SELECTOR)) {
          wirePopover(n);
        }
        // If deeper children contain [popover]
        n.querySelectorAll?.(POPOVER_SELECTOR).forEach((el) =>
          wirePopover(el as HTMLElement)
        );
      }
    });
    m.removedNodes.forEach((n) => {
      if (n instanceof HTMLElement) {
        const list: Element[] = n.matches?.(POPOVER_SELECTOR)
          ? [n]
          : Array.from(n.querySelectorAll?.(POPOVER_SELECTOR) || []);

        list.forEach((el) => {
          const htmlEl = el as HTMLElement;
          const state = popoverStates.get(htmlEl);
          if (state) {
            htmlEl.removeEventListener('toggle', state.onToggle);
          }
          cleanupPopoverState(htmlEl);
          popoverStates.delete(htmlEl);
        });
      }
    });
  });
});

/**
 * Initialization: wire up existing popovers and observe the DOM.
 */
function initPopoverPositioning() {
  document.querySelectorAll<HTMLElement>(POPOVER_SELECTOR).forEach(wirePopover);
  mo.observe(document.documentElement, { childList: true, subtree: true });
}

// Start automatically
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPopoverPositioning);
} else {
  initPopoverPositioning();
}
